import React, { ReactElement } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { CustomDialog } from '../../../components/CustomDialog';
import { RETRO_TEMPLATES } from '../templates';
import TemplateArt from '../templates/art/TemplateArt';

export interface TemplatePickerDialogProps {
  open: boolean;
  activeTemplateId: string | null;
  isDarkMode: boolean;
  onClose: () => void;
  onSelect: (templateId: string | null) => void;
}

export default function TemplatePickerDialog({
  open,
  activeTemplateId,
  isDarkMode,
  onClose,
  onSelect,
}: TemplatePickerDialogProps): ReactElement {
  return (
    <CustomDialog open={open} onClosed={onClose} maxWidth="md" fullWidth>
      <Box sx={{ width: '100%', py: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Choose a retrospective template
        </Typography>
        <Grid container spacing={2}>
          {RETRO_TEMPLATES.map(template => {
            const isActive = activeTemplateId === template.id;
            const isComingSoon = template.comingSoon === true;
            const accent = isDarkMode
              ? (template.accentDark ?? '#90caf9')
              : (template.accentLight ?? '#1976d2');
            return (
              <Grid size={{ xs: 12, sm: 6 }} key={template.id}>
                <Box
                  onClick={isComingSoon ? undefined : () => onSelect(template.id)}
                  sx={{
                    position: 'relative',
                    border: `2px solid ${isActive ? accent : 'divider'}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                    cursor: isComingSoon ? 'default' : 'pointer',
                    bgcolor: 'background.paper',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    boxShadow: isActive ? `0 0 0 1px ${accent}` : 'none',
                    '&:hover': isComingSoon ? undefined : { borderColor: accent },
                  }}
                >
                  <Box sx={{ height: 200, overflow: 'hidden', bgcolor: isDarkMode ? '#1e1e1e' : '#f5f5f5' }}>
                    <TemplateArt templateId={template.id} isDarkMode={isDarkMode} compact />
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2">{template.name}</Typography>
                    {template.tagline && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        {template.tagline}
                      </Typography>
                    )}
                  </Box>
                  {isComingSoon && (
                    <Box
                      aria-label="Coming soon"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)',
                        backdropFilter: 'saturate(120%) blur(1px)',
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          px: 2,
                          py: 0.5,
                          borderRadius: 5,
                          fontWeight: 700,
                          letterSpacing: 1,
                          color: isDarkMode ? '#fff' : '#000',
                          bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)',
                          border: '1px solid',
                          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)',
                        }}
                      >
                        Coming soon
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
        {activeTemplateId && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button color="inherit" onClick={() => onSelect(null)}>
              Remove template
            </Button>
          </Box>
        )}
      </Box>
    </CustomDialog>
  );
}
